import React, { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';

import { useSalonPublicTheme } from '../../../context/SalonPublicThemeContext';


const compositionStyles = {
  horizontal: {
    display: 'grid',
    gridTemplateColumns: 'auto minmax(0, 1fr) auto',
    gridTemplateAreas: '"brand navigation actions"',
    alignItems: 'center',
    gap: 3,
  },
  centered: {
    display: 'grid',
    gridTemplateColumns: '1fr auto 1fr',
    gridTemplateAreas: '"navigation brand actions"',
    alignItems: 'center',
    gap: 3,
  },
  compact: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    gridTemplateAreas: '"brand actions" "navigation navigation"',
    alignItems: 'center',
    gap: 1.5,
  },
};

const SalonPublicHeader = ({
  brand,
  navigation,
  actions,
  heroBoundaryRef,
  hasHeroMedia = false,
  style,
  sx,
  ...props
}) => {
  const { layout, tokens: theme } = useSalonPublicTheme();
  const headerTokens = layout.tokens.header;
  const isNarrow = useMediaQuery('(max-width:899.95px)', { noSsr: true });
  const [pastHero, setPastHero] = useState(false);
  const effectiveComposition = isNarrow ? 'compact' : headerTokens.composition;
  const shouldObserveHero = (
    headerTokens.surface === 'transparent'
    && headerTokens.behavior === 'sticky'
    && hasHeroMedia
  );

  useEffect(() => {
    if (
      !shouldObserveHero
      || !heroBoundaryRef?.current
      || typeof IntersectionObserver === 'undefined'
    ) {
      setPastHero(false);
      return undefined;
    }

    const observer = new IntersectionObserver(([entry]) => {
      setPastHero(!entry.isIntersecting);
    });
    observer.observe(heroBoundaryRef.current);
    return () => observer.disconnect();
  }, [heroBoundaryRef, shouldObserveHero]);

  const effectiveSurface = useMemo(() => {
    if (headerTokens.surface !== 'transparent') return headerTokens.surface;
    if (!hasHeroMedia) return 'brand';
    if (pastHero) return 'light';
    return 'transparent';
  }, [hasHeroMedia, headerTokens.surface, pastHero]);

  const surfaceStyles = {
    light: {
      backgroundColor: theme.salon.surface,
      color: theme.salon.onBackground,
      borderColor: theme.salon.border,
    },
    brand: {
      backgroundColor: theme.salon.primary,
      color: theme.salon.onPrimary,
      borderColor: 'transparent',
    },
    transparent: {
      backgroundColor: 'transparent',
      color: hasHeroMedia ? '#FFFFFF' : theme.salon.onBackground,
      borderColor: 'transparent',
    },
  }[effectiveSurface];
  const headerHeight = effectiveComposition === 'compact' ? '64px' : '72px';

  return (
    <Box
      {...props}
      component="header"
      dir={layout.tokens.direction}
      data-composition={headerTokens.composition}
      data-effective-composition={effectiveComposition}
      data-surface={headerTokens.surface}
      data-effective-surface={effectiveSurface}
      data-behavior={headerTokens.behavior}
      data-past-hero={String(pastHero)}
      style={{
        ...style,
        '--salon-public-header-height': headerHeight,
        position: headerTokens.position,
        top: headerTokens.position === 'sticky' ? 0 : undefined,
        backgroundColor: surfaceStyles.backgroundColor,
        color: surfaceStyles.color,
      }}
      sx={{
        zIndex: 1100,
        width: '100%',
        borderBottom: '1px solid',
        borderColor: surfaceStyles.borderColor,
        transition: 'background-color 180ms ease, color 180ms ease, border-color 180ms ease',
        ...sx,
      }}
    >
      <Box
        data-testid="salon-public-header-inner"
        sx={{
          minHeight: headerHeight,
          width: 'min(100% - 32px, 1200px)',
          mx: 'auto',
          py: effectiveComposition === 'compact' ? 1 : 0,
          ...compositionStyles[effectiveComposition],
        }}
      >
        <Box data-testid="salon-public-header-brand" sx={{ gridArea: 'brand', minWidth: 0 }}>
          {brand}
        </Box>
        <Box
          data-testid="salon-public-header-navigation"
          sx={{
            gridArea: 'navigation',
            minWidth: 0,
            justifySelf: effectiveComposition === 'horizontal' ? 'center' : 'stretch',
          }}
        >
          {navigation}
        </Box>
        <Box
          data-testid="salon-public-header-actions"
          sx={{ gridArea: 'actions', justifySelf: 'end' }}
        >
          {actions}
        </Box>
      </Box>
    </Box>
  );
};

export default SalonPublicHeader;
